import { Dispatch, SetStateAction } from 'react';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { scroller } from 'react-scroll';
import { useMutation } from '@tanstack/react-query';
import { EventFormData, SectionTab } from '../types';
import toastMsg from '../../../../lib/toastMsg';
import EventApiV2 from '../../../../lib/api/event/EventApiV2';
import { asyncSimpleModal, reDirectPage } from '../../../../lib/common';
import { EventsCreateRequestBodyDto } from '../../../../types/api/events';
import { EVENT_SAVE_RESULT } from '../../../../constants/service/app/eventConstants';
import eventToastMsg from '../../../../constants/service/toastMsg/eventToastMsg';
import {
  EVENT_ALL_LIST_URL_PATH,
  EVENT_IN_PROGRESS_LIST_URL_PATH,
} from '../../../../constants/service/common/urlConstants';
import { convertSubmitRequestData } from '../utils';

interface UseSubmitProps extends FormComponentProps {
  id?: string;
  setEventSectionTabKey: Dispatch<SetStateAction<number | null>>;
  setEventSaveResult: Dispatch<SetStateAction<(typeof EVENT_SAVE_RESULT)[keyof typeof EVENT_SAVE_RESULT]>>;
}
const useSubmit = (props: UseSubmitProps) => {
  const { form, id, setEventSectionTabKey, setEventSaveResult } = props;

  const { mutate: createEvent, isLoading: isCreating } = useMutation(
    async (payload: EventsCreateRequestBodyDto) => EventApiV2.insertEvent(payload),
    {
      onSuccess: () => {
        setEventSaveResult(EVENT_SAVE_RESULT.SUCCESS);
        toastMsg.success(eventToastMsg.INSERT_EVENT_REQUEST_SUCCESS);
        reDirectPage({ path: EVENT_ALL_LIST_URL_PATH });
      },
      onError: (error) => {
        toastMsg.error(eventToastMsg.INSERT_EVENT_REQUEST_ERROR, error);
      },
    },
  );

  const { mutate: editEvent, isLoading: isEditing } = useMutation(
    async ({ id, data }: { id: string; data: EventsCreateRequestBodyDto }) => EventApiV2.editEvent({ id, data }),
    {
      onSuccess: () => {
        setEventSaveResult(EVENT_SAVE_RESULT.SUCCESS);
        toastMsg.success(eventToastMsg.EDIT_EVENT_REQUEST_SUCCESS);
        reDirectPage({ path: EVENT_IN_PROGRESS_LIST_URL_PATH });
      },
      onError: (error) => {
        toastMsg.error(eventToastMsg.EDIT_EVENT_REQUEST_ERROR, error);
      },
    },
  );

  const onSubmit = () => {
    form.validateFields(async (error, value: EventFormData) => {
      if (error) {
        if (error?.mainTitle || error?.contents || error?.memo || error?.eventAttachment) {
          scroller.scrollTo('event-info', { offset: -110 });
          return;
        }

        const sectionErrorKeys = Object.keys(error).filter((key) => key.match('section-item'));
        if (sectionErrorKeys.length > 0) {
          const target = value?.sectionTabList.find((tab: SectionTab) => {
            return sectionErrorKeys.find((key) => key.match(tab.name));
          });
          setEventSectionTabKey(target?.id ?? null);
          scroller.scrollTo('section-list', { offset: 30 });
          return;
        }

        return;
      }

      /**
       * 섹션 이동 탭은 사용하지 않거나 2개 이상부터 등록 가능
       */
      const sectionTabTitleCnt = Object.keys(value).filter((key) => key.match('tabTitle') && !!value[key]).length;
      if (sectionTabTitleCnt === 1) {
        await asyncSimpleModal({
          type: 'error',
          title: '섹션 이동 탭 설정',
          message: '섹션 이동 탭 사용 시, 기획전 별로 최소 2개의\n섹션에 사용해야 합니다.',
        });
        scroller.scrollTo('section-list', { offset: 30 });
        return;
      }

      const requestData = convertSubmitRequestData(value, sectionTabTitleCnt > 0);
      if (!id) {
        createEvent(requestData);
      } else {
        editEvent({ id, data: requestData });
      }
    });
  };

  return {
    onSubmit,
    isSubmitting: isCreating || isEditing,
  };
};

export default useSubmit;
